import { ref, computed, watch } from 'vue'

/**
 * 統一的分頁管理 composable
 * 提供持久化的分頁數據量保存功能
 */
export function usePagination(options = {}) {
  // 預設選項
  const defaultOptions = {
    defaultPageSize: 20,
    pageSizeOptions: ['10', '20', '50', '100'],
    storageKey: 'admin_global_pageSize'
  }
  
  const config = { ...defaultOptions, ...options }
  
  // 從 localStorage 讀取保存的分頁大小，如果沒有則使用預設值
  const getStoredPageSize = () => {
    try {
      const stored = localStorage.getItem(config.storageKey)
      if (stored) {
        const parsed = parseInt(stored)
        // 驗證是否為有效的分頁大小選項
        if (!isNaN(parsed) && config.pageSizeOptions.includes(parsed.toString())) {
          return parsed
        }
      }
    } catch (error) {
      console.warn('讀取分頁設定失敗:', error)
    }
    return config.defaultPageSize
  }
  
  // 響應式狀態
  const currentPage = ref(1)
  const pageSize = ref(getStoredPageSize())
  const totalItems = ref(0)
  
  // 計算屬性
  const totalPages = computed(() => {
    return Math.ceil(totalItems.value / pageSize.value)
  })
  
  // 監聽分頁大小變化，自動保存到 localStorage
  watch(pageSize, (newSize) => {
    try {
      localStorage.setItem(config.storageKey, newSize.toString())
    } catch (error) {
      console.warn('保存分頁設定失敗:', error)
    }
  }, { immediate: false })
  
  // Ant Design 分頁配置
  const paginationConfig = computed(() => ({
    current: currentPage.value,
    pageSize: pageSize.value,
    total: totalItems.value,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `顯示 ${range[0]}-${range[1]} 筆，共 ${total} 筆資料`,
    pageSizeOptions: config.pageSizeOptions,
    size: 'default'
  }))
  
  // 滾動到頂部的函數
  const scrollToTop = () => {
    window.scrollTo({
      top: 0
      // 移除 behavior: 'smooth' 以避免與其他動畫衝突
    });
    // 後台內容捲動的是內容區容器，視窗本身不會捲動
    const contentArea = document.querySelector('.tab-content');
    if (contentArea) {
      contentArea.scrollTop = 0;
    }
  };

  // 處理分頁變化
  const handlePageChange = (page, size) => {
    currentPage.value = page
    if (size && size !== pageSize.value) {
      pageSize.value = size
    }
  }

  // 處理分頁大小變化
  const handlePageSizeChange = (current, size) => {
    currentPage.value = 1 // 改變分頁大小時重置到第一頁
    pageSize.value = size
  }

  // 帶置頂功能的分頁變化處理
  const handlePageChangeWithScroll = (page, size) => {
    handlePageChange(page, size)
    scrollToTop()
  }

  // 帶置頂功能的分頁大小變化處理
  const handlePageSizeChangeWithScroll = (current, size) => {
    handlePageSizeChange(current, size)
    scrollToTop()
  }
  
  // 重置分頁
  const resetPagination = () => {
    currentPage.value = 1
    totalItems.value = 0
  }
  
  // 設定總數據量
  const setTotal = (total) => {
    totalItems.value = total
    
    // 檢查當前頁碼是否超出範圍
    if (currentPage.value > totalPages.value && totalPages.value > 0) {
      currentPage.value = totalPages.value
    }
  }
  
  return {
    // 響應式狀態
    currentPage,
    pageSize,
    totalItems,
    totalPages,

    // 配置
    paginationConfig,

    // 方法
    handlePageChange,
    handlePageSizeChange,
    handlePageChangeWithScroll,
    handlePageSizeChangeWithScroll,
    scrollToTop,
    resetPagination,
    setTotal
  }
}
